import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth.jsx";
import api from "@/lib/api";
import { toast } from "sonner";
import { LogOut, GraduationCap, Calendar, FileText, Ticket as TicketIcon, DollarSign, User as UserIcon, HelpCircle } from "lucide-react";
import { BrandMark } from "@/components/BrandMark.jsx";

function todayLabel() {
  const d = new Date();
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  return `${String(d.getDate()).padStart(2,"0")} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

const TABS = [
  { k: "overview", label: "Overview", icon: UserIcon },
  { k: "leaves", label: "Leaves", icon: Calendar },
  { k: "training", label: "Training", icon: GraduationCap },
  { k: "policies", label: "Policies", icon: FileText },
  { k: "payslips", label: "Payslips", icon: DollarSign },
  { k: "tickets", label: "Tickets", icon: TicketIcon },
];

export default function Portal() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const [leaves, setLeaves] = useState(null);
  const [training, setTraining] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [payslips, setPayslips] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [newLeave, setNewLeave] = useState(null);
  const [newTicket, setNewTicket] = useState(null);

  const load = async () => {
    try {
      const [m, l, t, p, tk] = await Promise.all([
        api.get("/portal/me"), api.get("/portal/my-leaves"),
        api.get("/portal/my-training"), api.get("/portal/my-policies"),
        api.get("/tickets"),
      ]);
      setMe(m.data); setLeaves(l.data); setTraining(t.data); setPolicies(p.data); setTickets(tk.data);
      if (m.data.employee) {
        const ps = await api.get(`/payroll/payslips/${m.data.employee.employee_id}`);
        setPayslips(ps.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load portal");
    }
  };
  useEffect(() => { load(); }, []);

  const applyLeave = async () => {
    try {
      await api.post("/portal/my-leaves", newLeave);
      toast.success("Leave requested — awaiting founder approval");
      setNewLeave(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const fileTicket = async () => {
    try {
      await api.post("/tickets", newTicket);
      toast.success("Ticket filed");
      setNewTicket(null); load();
    } catch { toast.error("Failed"); }
  };
  const ackPolicy = async (p) => {
    await api.post("/policies/ack", { policy_id: p.policy_id, version: p.latest_version });
    toast.success("Acknowledged"); load();
  };
  const ackSop = async (a, mod) => {
    const latest = mod.versions[mod.versions.length - 1];
    await api.post("/training/ack-sop", { assignment_id: a.assignment_id, version: latest.version });
    toast.success("SOP acknowledged"); load();
  };

  if (!me) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[#71717A] font-mono text-xs">LOADING…</div>;

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F4F4F5]">
      <header className="sticky top-0 z-30 bg-[#0A0A0A] border-b border-[#1E1E1E]">
        <div className="max-w-[1440px] mx-auto px-8 h-16 flex items-center gap-8">
          <div className="flex items-center shrink-0">
            <BrandMark size="lg" />
          </div>
          <span className="hidden md:inline-flex font-mono text-[10px] tracking-[0.22em] text-[#71717A] uppercase">
            MY PORTAL
          </span>
          <div className="flex-1" />
          <span className="hidden md:inline-flex font-mono text-[10px] tracking-[0.22em] text-[#71717A] uppercase">
            {todayLabel()}
          </span>
          <div className="flex items-center gap-2.5 pl-3 border-l border-[#1E1E1E]">
            <div className="h-8 w-8 rounded-full bg-[#1A1A1A] border border-[#262626] grid place-items-center text-xs font-medium">{initial}</div>
            <div className="hidden md:block text-left leading-tight">
              <div className="text-xs text-[#F4F4F5]">{(user?.name || user?.email || "").split("@")[0]}</div>
              <div className="font-mono text-[9px] tracking-[0.22em] text-[#71717A] uppercase">EMPLOYEE</div>
            </div>
          </div>
          <button className="tt-btn-ghost p-1.5 text-[#71717A] hover:text-[#F4F4F5]" data-testid="portal-help"><HelpCircle size={15} strokeWidth={1.6} /></button>
          <button onClick={logout} className="tt-btn-ghost p-1.5 text-[#71717A] hover:text-[#F4F4F5]" data-testid="portal-logout"><LogOut size={15} strokeWidth={1.6} /></button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{me.employee?.display_name || me.user?.name}</h1>
            {me.employee && <div className="text-sm text-[#A1A1AA]">{me.employee.role_title} · {me.employee.team || "no team"} · <span className="font-mono text-xs">tenure {me.employee.tenure_days}d</span></div>}
          </div>
        </div>

        <div className="flex gap-1 border-b border-[#1E1E1E] mb-6 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`portal-tab-${t.k}`}
              className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 ${tab === t.k ? "border-[#5B9BFF] text-[#F4F4F5]" : "border-transparent text-[#A1A1AA] hover:text-[#F4F4F5]"}`}>
              <t.icon size={14} strokeWidth={1.75} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && me.employee && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="tt-card p-5">
              <div className="tt-label mb-3">Profile</div>
              <div className="text-sm space-y-1">
                <div><span className="text-[#71717A]">Role:</span> {me.employee.role_title}</div>
                <div><span className="text-[#71717A]">Team:</span> {me.employee.team || "—"}</div>
                <div><span className="text-[#71717A]">Joining:</span> <span className="font-mono">{me.employee.joining_date}</span></div>
                <div><span className="text-[#71717A]">Status:</span> {me.employee.ready_at ? <span className="tt-badge-pass">Ready</span> : <span className="tt-badge-info">{me.employee.status}</span>}</div>
              </div>
            </div>
            <div className="tt-card p-5">
              <div className="tt-label mb-3">Leave balance</div>
              {leaves?.balances?.map((b) => (
                <div key={b.type_id} className="flex items-center justify-between py-1 border-b border-[#1E1E1E] last:border-0">
                  <span className="text-sm">{b.name}</span>
                  <span className="font-mono text-sm">{b.balance} / {b.quota}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "overview" && !me.employee && (
          <div className="tt-card p-5 text-sm text-[#A1A1AA]">You don't have an employee record yet. If you believe this is wrong, email HR.</div>
        )}

        {tab === "leaves" && (
          <>
            <div className="flex justify-end mb-4"><button onClick={() => setNewLeave({ type_id: leaves?.balances?.[0]?.type_id || "", start_date: "", end_date: "", half_day: false, reason: "" })} className="tt-btn-primary text-sm">Request leave</button></div>
            <div className="tt-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#0F0F0F]"><tr className="text-[#71717A] text-xs font-mono"><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Dates</th><th className="text-right px-4 py-2">Days</th><th className="text-left px-4 py-2">Status</th></tr></thead>
                <tbody>
                  {leaves?.requests?.map((r) => (
                    <tr key={r.request_id} className="border-t border-[#1E1E1E]">
                      <td className="px-4 py-3">{(leaves.balances.find((b) => b.type_id === r.type_id) || {}).name || "?"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.start_date} → {r.end_date}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.days}</td>
                      <td className="px-4 py-3">
                        <span className={r.status === "approved" ? "tt-badge-pass" : r.status === "rejected" ? "tt-badge-danger" : "tt-badge-warn"}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                  {(!leaves?.requests || leaves.requests.length === 0) && <tr><td colSpan={4} className="px-4 py-10 text-center text-[#71717A]">No requests yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "training" && training && (
          <div className="space-y-3">
            {training.ready_at && <div className="tt-card p-4 border-[#22C55E]/40"><div className="tt-badge-pass inline-block">READY</div> <span className="ml-2 text-sm text-[#A1A1AA]">Marked ready on {training.ready_at.slice(0,10)}</span></div>}
            {training.assignments.length === 0 && <div className="text-sm text-[#71717A]">No modules assigned yet.</div>}
            {training.assignments.map((a) => {
              const mod = training.modules.find((m) => m.module_id === a.module_id);
              if (!mod) return null;
              const latestV = mod.versions[mod.versions.length - 1];
              const acked = a.sop_acknowledged_version === latestV.version;
              return (
                <div key={a.assignment_id} className="tt-card p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium">{mod.title}</div>
                    <div className="flex gap-1">
                      <span className={a.status === "passed" ? "tt-badge-pass" : a.status === "failed" ? "tt-badge-danger" : "tt-badge-info"}>{a.status.replace("_"," ")}</span>
                    </div>
                  </div>
                  <div className="text-xs text-[#71717A] mb-2 font-mono">{mod.estimated_minutes}m · pass {mod.pass_mark_pct}% · attempts {a.attempts}/{mod.max_attempts}{a.best_score_pct ? ` · best ${a.best_score_pct}%` : ""}</div>
                  {a.needs_reack_version && <div className="tt-badge-warn mb-2">SOP updated — please re-read and re-ack</div>}
                  <details className="text-sm">
                    <summary className="cursor-pointer text-[#5B9BFF]">Read SOP (v{latestV.version})</summary>
                    <pre className="font-mono text-xs whitespace-pre-wrap mt-2 bg-[#0F0F0F] p-3 rounded">{latestV.sop_markdown}</pre>
                  </details>
                  {!acked && <button onClick={() => ackSop(a, mod)} className="tt-btn-secondary text-xs mt-2">I've read the SOP — unlock quiz</button>}
                  {acked && <TakeQuiz assignment={a} module={mod} onDone={load} />}
                </div>
              );
            })}
          </div>
        )}

        {tab === "policies" && (
          <div className="space-y-3">
            {policies.map((p) => {
              const latest = p.versions[p.versions.length - 1];
              return (
                <div key={p.policy_id} className="tt-card p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium">{p.title}</div>
                    <div className="flex gap-1">
                      <span className="tt-badge-neutral">v{latest.version}</span>
                      {p.needs_ack ? <span className="tt-badge-warn">Needs acknowledgement</span> : <span className="tt-badge-pass">Acknowledged</span>}
                    </div>
                  </div>
                  <details className="text-sm">
                    <summary className="cursor-pointer text-[#5B9BFF]">Read</summary>
                    <pre className="font-mono text-xs whitespace-pre-wrap mt-2 bg-[#0F0F0F] p-3 rounded">{latest.body_markdown}</pre>
                  </details>
                  {p.needs_ack && <button onClick={() => ackPolicy(p)} className="tt-btn-primary text-xs mt-2">I acknowledge</button>}
                </div>
              );
            })}
          </div>
        )}

        {tab === "payslips" && (
          <div className="tt-card p-5">
            {payslips?.payslips?.length === 0 && <div className="text-sm text-[#71717A]">No payslips available. <a href={payslips?.portal_url} className="text-[#5B9BFF] underline">Open Zoho Payroll</a> to view.</div>}
            {payslips?.payslips?.map((s) => (
              <div key={s.period} className="flex items-center justify-between py-2 border-b border-[#1E1E1E] last:border-0">
                <div>
                  <div className="font-mono">{s.period}</div>
                  <div className="text-xs text-[#71717A]">source: {s.source}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono">net ₹{s.net}</div>
                  <a href={s.pdf_url} target="_blank" rel="noreferrer" className="text-[#5B9BFF] text-xs underline">open</a>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "tickets" && (
          <>
            <div className="flex justify-end mb-4"><button onClick={() => setNewTicket({ kind: "suggestion", title: "", body: "", anonymous: false })} className="tt-btn-primary text-sm">File ticket</button></div>
            <div className="tt-card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#0F0F0F]"><tr className="text-[#71717A] text-xs font-mono"><th className="text-left px-4 py-2">Title</th><th className="text-left px-4 py-2">Kind</th><th className="text-left px-4 py-2">Status</th></tr></thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr key={t.ticket_id} className="border-t border-[#1E1E1E]">
                      <td className="px-4 py-3">{t.title}</td>
                      <td className="px-4 py-3">{t.kind}</td>
                      <td className="px-4 py-3"><span className={t.status === "resolved" || t.status === "closed" ? "tt-badge-pass" : "tt-badge-warn"}>{t.status}</span></td>
                    </tr>
                  ))}
                  {tickets.length === 0 && <tr><td colSpan={3} className="px-4 py-10 text-center text-[#71717A]">No tickets yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {newLeave && (
        <Modal onClose={() => setNewLeave(null)} title="Request leave">
          <select className="tt-input mb-3" value={newLeave.type_id} onChange={(e) => setNewLeave({ ...newLeave, type_id: e.target.value })}>
            {leaves?.balances?.map((b) => <option key={b.type_id} value={b.type_id}>{b.name} · balance {b.balance}</option>)}
          </select>
          <input type="date" className="tt-input mb-3" value={newLeave.start_date} onChange={(e) => setNewLeave({ ...newLeave, start_date: e.target.value })} />
          <input type="date" className="tt-input mb-3" value={newLeave.end_date} onChange={(e) => setNewLeave({ ...newLeave, end_date: e.target.value })} />
          <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" className="accent-[#5B9BFF]" checked={newLeave.half_day} onChange={(e) => setNewLeave({ ...newLeave, half_day: e.target.checked })} /> Half day</label>
          <div className="tt-label mb-1">Backup during leave <span className="text-[#71717A] normal-case">· teammate who'll cover</span></div>
          <input className="tt-input mb-3 text-sm" placeholder="Teammate email (e.g. prakhar@thickandthin.media)" value={newLeave.backup_employee_id || ""} onChange={(e) => setNewLeave({ ...newLeave, backup_employee_id: e.target.value })} />
          <textarea rows={3} className="tt-input mb-4" placeholder="Reason" value={newLeave.reason} onChange={(e) => setNewLeave({ ...newLeave, reason: e.target.value })} />
          <div className="flex justify-end gap-2"><button onClick={() => setNewLeave(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={applyLeave} className="tt-btn-primary text-sm">Submit</button></div>
        </Modal>
      )}
      {newTicket && (
        <Modal onClose={() => setNewTicket(null)} title="File ticket">
          <select className="tt-input mb-3" value={newTicket.kind} onChange={(e) => setNewTicket({ ...newTicket, kind: e.target.value })}>
            {["suggestion", "complaint", "grievance", "posh"].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input className="tt-input mb-3" placeholder="Title" value={newTicket.title} onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })} />
          <textarea rows={4} className="tt-input mb-3" placeholder="Details" value={newTicket.body} onChange={(e) => setNewTicket({ ...newTicket, body: e.target.value })} />
          <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" className="accent-[#5B9BFF]" checked={newTicket.anonymous} onChange={(e) => setNewTicket({ ...newTicket, anonymous: e.target.checked })} /> File anonymously (no identity stored)</label>
          <div className="flex justify-end gap-2"><button onClick={() => setNewTicket(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={fileTicket} className="tt-btn-primary text-sm">File</button></div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={onClose}>
      <div className="w-full max-w-md tt-card p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function TakeQuiz({ assignment, module: mod, onDone }) {
  const approved = useMemo(() => (mod.questions || []).filter((q) => q.approved), [mod]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  if (approved.length === 0) return <div className="text-xs text-[#71717A] mt-2">Quiz not published yet.</div>;
  if (assignment.status === "passed") return <div className="text-xs text-[#22C55E] mt-2 font-mono">Passed with {assignment.best_score_pct}%</div>;

  const submit = async () => {
    const r = await api.post("/training/submit-quiz", { assignment_id: assignment.assignment_id, answers });
    setResult(r.data);
    toast[r.data.passed ? "success" : "warning"](`${r.data.passed ? "Passed" : "Not yet"} · ${r.data.score_pct}%`);
    onDone();
  };

  return (
    <div className="mt-3 border-t border-[#1E1E1E] pt-3 space-y-3">
      {approved.map((q) => (
        <div key={q.question_id}>
          <div className="text-sm mb-1">{q.prompt}</div>
          {q.kind === "mcq" && q.options.map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm py-0.5">
              <input type="radio" name={q.question_id} value={o} checked={answers[q.question_id] === o} onChange={() => setAnswers({ ...answers, [q.question_id]: o })} className="accent-[#5B9BFF]" />{o}
            </label>
          ))}
          {q.kind === "truefalse" && ["True", "False"].map((o) => (
            <label key={o} className="inline-flex items-center gap-2 text-sm mr-4">
              <input type="radio" name={q.question_id} value={o} checked={answers[q.question_id] === o} onChange={() => setAnswers({ ...answers, [q.question_id]: o })} className="accent-[#5B9BFF]" />{o}
            </label>
          ))}
          {["short", "long", "scenario"].includes(q.kind) && (
            <textarea rows={q.kind === "short" ? 2 : 4} className="tt-input" value={answers[q.question_id] || ""} onChange={(e) => setAnswers({ ...answers, [q.question_id]: e.target.value })} />
          )}
        </div>
      ))}
      <button onClick={submit} className="tt-btn-primary text-sm">Submit quiz</button>
      {result && (
        <div className="text-xs font-mono text-[#A1A1AA]">
          Score: {result.score_pct}% · {result.passed ? "Pass" : "Try again"}
        </div>
      )}
    </div>
  );
}
